import * as React from 'react';
import { Navigate, Outlet, Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AppShell } from '@/components/layout/app-shell';
import { AccountsPage } from '@/features/accounts/accounts-page';
import { AuthPage } from '@/features/auth/auth-page';
import { ComposerPage } from '@/features/composer/composer-page';
import { DashboardPage } from '@/features/dashboard/dashboard-page';
import { QueuePage } from '@/features/queue/queue-page';
import { SettingsPage } from '@/features/settings/settings-page';
import { useAuthStore } from '@/stores/auth-store';
import { applyTheme, useUiStore, watchSystemTheme } from '@/stores/ui-store';

export default function App() {
  const initialize = useAuthStore((s) => s.initialize);
  const theme = useUiStore((s) => s.theme);

  React.useEffect(() => {
    // Returns an unsubscribe — without it, a hot reload stacks up auth listeners
    // and every state change fires N times.
    return initialize();
  }, [initialize]);

  React.useEffect(() => {
    // Zustand's `persist` rehydrates the stored theme *after* first render, so
    // re-apply it here. The inline script in index.html already prevented the
    // flash; this keeps the store and the DOM in agreement afterwards.
    applyTheme(theme);
    return watchSystemTheme();
  }, [theme]);

  return (
    <Router>
      <Routes>
        <Route path="/auth" element={<AuthPage />} />

        <Route element={<ProtectedRoute />}>
          <Route element={<AppShell />}>
            <Route path="/" element={<DashboardPage />} />
            <Route path="/compose" element={<ComposerPage />} />
            <Route path="/queue" element={<QueuePage />} />
            <Route path="/accounts" element={<AccountsPage />} />
            <Route path="/settings" element={<SettingsPage />} />
          </Route>
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      <Toaster
        position="bottom-right"
        // Inherit the app's own tokens rather than sonner's defaults, so a toast
        // in dark mode is not a white rectangle.
        toastOptions={{
          classNames: {
            toast: 'bg-card text-card-foreground border-border shadow-lg',
            description: 'text-muted-foreground',
            actionButton: 'bg-primary text-primary-foreground',
          },
        }}
        closeButton
      />
    </Router>
  );
}

/**
 * Gates the authenticated app.
 *
 * The `initializing` check is load-bearing: on a hard refresh we have a stored
 * token but have not yet confirmed it with the API, so there is a brief moment
 * where the user is signed in but `user` is still null. Redirecting during that
 * window throws an authenticated user out to the login page on every refresh.
 */
function ProtectedRoute() {
  const user = useAuthStore((s) => s.user);
  const initializing = useAuthStore((s) => s.initializing);

  if (initializing) {
    // Deliberately blank, not a spinner. This resolves in a few milliseconds
    // from localStorage; a spinner would flash and read as jank.
    return <div className="bg-background h-dvh" />;
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // Renders the nested <AppShell />.
  return <Outlet />;
}
